<template>
  <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
    <header class="mb-8">
      <h1
        class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100 text-center sm:text-left">
        Relatório
        <span class="bg-clip-text text-transparent bg-gradient-to-r from-[#7B2CBF] to-[#A367E1]">Financeiro</span>
      </h1>
      <p class="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left mt-1">
        Analise suas finanças com gráficos e dados detalhados.
      </p>
    </header>

    <div class="panel mb-8">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
        <div class="flex items-center gap-3">
          <label for="anoSelecionadoRel" class="form-label whitespace-nowrap">Ano:</label>
          <select id="anoSelecionadoRel" v-model="anoSelecionado" class="form-select flex-grow">
            <option v-for="ano in anosDisponiveis" :key="ano" :value="ano">{{ ano }}</option>
          </select>
        </div>
        <div class="flex items-center gap-3">
          <label for="mesSelecionadoRel" class="form-label whitespace-nowrap">Mês:</label>
          <select id="mesSelecionadoRel" v-model="mesSelecionado" class="form-select flex-grow">
            <option value="">Ano Inteiro</option>
            <option v-for="mes in meses" :key="mes.valor" :value="mes.valor">{{ mes.nome }}</option>
          </select>
        </div>
        <div class="col-span-2 lg:col-span-2 flex items-center justify-start sm:justify-end gap-4">
          <button @click="exportarPDF" :disabled="isExporting"
            class="btn btn-secondary text-rose-500 dark:text-rose-400">
            <span class="material-symbols-outlined">picture_as_pdf</span> PDF
          </button>
          <button @click="exportarExcel" :disabled="isExporting"
            class="btn btn-secondary text-emerald-500 dark:text-emerald-400">
            <span class="material-symbols-outlined">file_download</span> Excel
          </button>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
      <div class="stat-card">
        <span
          class="stat-icon bg-rose-100 dark:bg-rose-900/40 text-rose-500 dark:text-rose-400 material-symbols-outlined">trending_down</span>
        <div>
          <p class="stat-label">Total de Despesas</p>
          <p class="stat-value text-rose-500 dark:text-rose-400">{{ formatCurrency(totalDespesasPeriodo) }}</p>
        </div>
      </div>
      <div class="stat-card">
        <span
          class="stat-icon bg-emerald-100 dark:bg-emerald-900/40 text-emerald-500 dark:text-emerald-400 material-symbols-outlined">trending_up</span>
        <div>
          <p class="stat-label">Total de Receitas</p>
          <p class="stat-value text-emerald-500 dark:text-emerald-400">{{ formatCurrency(totalReceitasPeriodo) }}</p>
        </div>
      </div>
      <div class="stat-card sm:col-span-2 lg:col-span-1">
        <span
          class="stat-icon bg-sky-100 dark:bg-sky-900/40 text-sky-500 dark:text-sky-400 material-symbols-outlined">account_balance</span>
        <div>
          <p class="stat-label">Balanço do Período</p>
          <p class="stat-value"
            :class="balancoPeriodo >= 0 ? 'text-emerald-500 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'">
            {{ formatCurrency(balancoPeriodo) }}</p>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
      <div class="panel">
        <h3 class="panel-title">Histórico de Gastos (Últimos 12 Meses)</h3>
        <div class="h-80">
          <Bar v-if="!isFetching" :data="historicoGastosData" :options="chartOptions" />
        </div>
      </div>
      <div class="panel">
        <h3 class="panel-title">Top 5 Categorias de Gastos (Curva ABC)</h3>
        <div class="h-80">
          <Bar v-if="temDadosCategoria" :data="gastosPorCategoriaABCData" :options="abcChartOptions" />
          <div v-else class="empty-state h-full">
            <span class="material-symbols-outlined text-5xl">pie_chart</span>
            <span>Sem dados de gastos para o período.</span>
          </div>
        </div>
      </div>
    </div>

    <div class="panel">
      <header class="mb-6">
        <h3 class="panel-title">
          Detalhes de Transações
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Filtre e analise todas as suas movimentações.</p>
        <div class="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <select v-model="statusFiltro" class="form-select">
            <option value="">Status</option>
            <option value="PENDENTE">Pendente</option>
            <option value="PAGO">Pago</option>
            <option value="CONGELADO">Congelado</option>
          </select>
          <select v-model="bancoFiltro" class="form-select">
            <option value="">Banco</option>
            <option v-for="banco in bancosDisponiveis" :key="banco" :value="banco">{{ banco }}</option>
          </select>
          <select v-model="categoriaFiltro" class="form-select">
            <option value="">Categoria</option>
            <option v-for="cat in categoriasDisponiveis" :key="cat" :value="cat">{{ cat }}</option>
          </select>
        </div>
      </header>

      <div class="overflow-x-auto hidden sm:block">
        <table class="w-full min-w-[800px] text-sm text-left">
          <thead class="text-xs text-slate-500 dark:text-slate-400 uppercase">
            <tr>
              <th class="px-6 py-3 font-semibold">Descrição</th>
              <th class="px-6 py-3 font-semibold text-center">Data</th>
              <th class="px-6 py-3 font-semibold text-center">Valor</th>
              <th class="px-6 py-3 font-semibold text-center">Categoria</th>
              <th class="px-6 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200/80 dark:divide-slate-700/60">
            <tr v-if="itensFiltradosParaLista.length === 0">
              <td colspan="5" class="empty-state h-48">
                Nenhuma transação encontrada.
              </td>
            </tr>
            <tr v-for="item in itensFiltradosParaLista" :key="item.uniqueId"
              class="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
              <td class="px-6 py-4 font-medium text-slate-900 dark:text-white whitespace-nowrap truncate max-w-sm">
                <div class="flex items-center gap-3">
                  <div class="w-2 h-8 rounded-full" :style="{ backgroundColor: getItemColor(item) }"></div>
                  <div>
                    <p class="truncate">{{ item.descricao }}</p>
                    <p class="text-xs text-slate-500 dark:text-slate-400">
                      {{ item.subtipo === 'cartao' ? `Cartão: ${item.banco}` : item.tipo }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center text-slate-500 dark:text-slate-400">{{
                formatDate(item.data) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-center font-bold"
                :class="item.tipo === 'Receita' ? 'text-emerald-500' : 'text-rose-500'">
                <span>{{ item.tipo === 'Receita' ? '+' : '-' }} {{ formatCurrency(item.valorParcela || item.valor)
                  }}</span>
                <div v-if="item.subtipo === 'cartao'" class="text-xs font-normal text-slate-500">Parc.
                  {{ item.numeroParcela }}/{{ item.totalParcelas }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center text-slate-500 dark:text-slate-400">
                <span v-if="item.categoria" :title="item.categoria" class="material-symbols-outlined text-lg">
                  {{ getCategoryIcon(item.categoria) }}
                </span>
                <span v-else>-</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="badge" :class="getStatusClass(item.status).bg">{{ item.status }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="block sm:hidden space-y-4">
        <div v-if="itensFiltradosParaLista.length === 0" class="empty-state mt-6">
          Nenhuma transação encontrada.
        </div>
        <div v-for="item in itensFiltradosParaLista" :key="`card-${item.uniqueId}`" class="mobile-card"
          :style="{ borderLeftColor: getItemColor(item) }">
          <div class="flex justify-between items-start">
            <div class="flex-1 pr-4">
              <p class="font-bold text-slate-800 dark:text-slate-100">{{ item.descricao }}</p>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ item.subtipo === 'cartao' ? `Cartão: ${item.banco}` : item.tipo }}
              </p>
            </div>
            <div class="text-right flex-shrink-0">
              <p class="font-bold text-lg" :class="item.tipo === 'Receita' ? 'text-emerald-500' : 'text-rose-500'">
                {{ item.tipo === 'Receita' ? '+' : '-' }} {{ formatCurrency(item.valorParcela || item.valor) }}
              </p>
              <p v-if="item.subtipo === 'cartao'" class="text-xs text-slate-500 dark:text-slate-400">
                Parc. {{ item.numeroParcela }}/{{ item.totalParcelas }}
              </p>
            </div>
          </div>
          <div
            class="mt-3 pt-3 border-t border-slate-200/80 dark:border-slate-700/60 flex items-center justify-between text-sm">
            <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
              <div v-if="item.categoria" class="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                <span class="material-symbols-outlined text-base">{{ getCategoryIcon(item.categoria) }}</span>
                <span>{{ item.categoria }}</span>
              </div>
              <div class="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                <span class="material-symbols-outlined text-base">event</span>
                <span>{{ formatDate(item.data) }}</span>
              </div>
            </div>
            <span class="badge" :class="getStatusClass(item.status).bg">{{ item.status }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const allItems = ref([]);
const isFetching = ref(true);
const isExporting = ref(false);

const mesSelecionado = ref('');
const anoSelecionado = ref('');
const statusFiltro = ref('');
const bancoFiltro = ref('');
const categoriaFiltro = ref('');

const isDarkMode = computed(() => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);

const meses = [
  { nome: 'Janeiro', valor: '01' }, { nome: 'Fevereiro', valor: '02' }, { nome: 'Março', valor: '03' },
  { nome: 'Abril', valor: '04' }, { nome: 'Maio', valor: '05' }, { nome: 'Junho', valor: '06' },
  { nome: 'Julho', valor: '07' }, { nome: 'Agosto', valor: '08' }, { nome: 'Setembro', valor: '09' },
  { nome: 'Outubro', valor: '10' }, { nome: 'Novembro', valor: '11' }, { nome: 'Dezembro', valor: '12' }
];

const bankColorVariants = {
  'Santander': { color: { light: '#EF0000', dark: '#F44336' } }, 'Nubank': { color: { light: '#820AD1', dark: '#9C27B0' } },
  'Banco do Brasil': { color: { light: '#FFDF00', dark: '#FFEB3B' } }, 'C6 Bank': { color: { light: '#333333', dark: '#CCCCCC' } },
  'Itaú': { color: { light: '#EC7000', dark: '#FF9800' } }, 'Bradesco': { color: { light: '#CC092F', dark: '#E53935' } },
  'Caixa': { color: { light: '#0064B4', dark: '#03A9F4' } }, 'Inter': { color: { light: '#FF7A00', dark: '#FFB74D' } },
  'PagBank': { color: { light: '#FFC800', dark: '#FFCA28' } }, 'PicPay': { color: { light: '#21C25E', dark: '#4CAF50' } },
  'Mercado Pago': { color: { light: '#00B2F2', dark: '#29B6F6' } }, 'Renner': { color: { light: '#D92A2F', dark: '#E53935' } },
  'Amazon': { color: { light: '#FF9900', dark: '#FFB300' } }, 'Shopee': { color: { light: '#EE4D2D', dark: '#FF7043' } },
  'Outro': { color: { light: '#94A3B8', dark: '#90A4AE' } }
};

const categoryIcons = {
  'Alimentação': 'restaurant', 'Transporte': 'commute', 'Moradia': 'home',
  'Lazer': 'sports_esports', 'Saúde': 'health_and_safety', 'Educação': 'school',
  'Compras': 'shopping_bag', 'Serviços': 'dns', 'Viagem': 'flight', 'Padrão': 'credit_card', 'Salário': 'payments'
};

const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fetchData = async () => {
  isFetching.value = true;
  try {
    const [despesasRes, receitasRes, gastosCartaoRes] = await Promise.all([
      axios.get('https://backend.controlease.com.br/gastos', authConfig()),
      axios.get('https://backend.controlease.com.br/receitas', authConfig()),
      axios.get('https://backend.controlease.com.br/gastos-cartao', authConfig())
    ]);
    const despesas = despesasRes.data.map(g => ({ ...g, uniqueId: `d-${g.id}`, tipo: 'Despesa', data: g.dataPagamento, subtipo: 'geral' }));
    const receitas = receitasRes.data.map(r => ({ ...r, uniqueId: `r-${r.id}`, tipo: 'Receita', data: r.dataRecebimento, subtipo: 'geral', categoria: r.categoria || 'Salário' }));
    const gastosCartao = gastosCartaoRes.data.map(g => ({ ...g, uniqueId: `c-${g.id}`, tipo: 'Despesa', data: g.dataPagamento, subtipo: 'cartao' }));
    allItems.value = [...despesas, ...receitas, ...gastosCartao];
  } catch (error) {
    console.error('Erro ao buscar dados:', error);
  } finally {
    isFetching.value = false;
  }
};

watch([anoSelecionado, mesSelecionado], fetchData, { immediate: true });

const itensFiltradosParaLista = computed(() => {
  return allItems.value.filter(item => {
    if (!item.data) return false;
    const [year, month] = item.data.split('-');
    const anoOk = !anoSelecionado.value || year === anoSelecionado.value;
    const mesOk = !mesSelecionado.value || month === mesSelecionado.value;
    const statusOk = !statusFiltro.value || item.status === statusFiltro.value;
    const categoriaOk = !categoriaFiltro.value || item.categoria === categoriaFiltro.value;
    const bancoOk = !bancoFiltro.value || (item.subtipo === 'cartao' && item.banco === bancoFiltro.value);
    return anoOk && mesOk && statusOk && categoriaOk && bancoOk;
  }).sort((a, b) => new Date(b.data) - new Date(a.data));
});

const itensFiltradosParaResumoEGraficos = computed(() => {
  return allItems.value.filter(item => {
    if (!item.data) return false;
    const [year, month] = item.data.split('-');
    const anoOk = !anoSelecionado.value || year === anoSelecionado.value;
    const mesOk = !mesSelecionado.value || month === mesSelecionado.value;
    return anoOk && mesOk;
  });
});

const totalDespesasPeriodo = computed(() => itensFiltradosParaResumoEGraficos.value.filter(i => i.tipo === 'Despesa').reduce((total, i) => total + parseFloat(i.valorParcela || i.valor || 0), 0));
const totalReceitasPeriodo = computed(() => itensFiltradosParaResumoEGraficos.value.filter(i => i.tipo === 'Receita').reduce((total, i) => total + parseFloat(i.valor || 0), 0));
const balancoPeriodo = computed(() => totalReceitasPeriodo.value - totalDespesasPeriodo.value);

const anosDisponiveis = computed(() => Array.from(new Set(allItems.value.map(i => i.data?.split('-')[0]).filter(Boolean))).sort((a, b) => b - a));
const bancosDisponiveis = computed(() => Array.from(new Set(allItems.value.filter(i => i.subtipo === 'cartao').map(i => i.banco).filter(Boolean))).sort());
const categoriasDisponiveis = computed(() => Array.from(new Set(allItems.value.map(i => i.categoria).filter(Boolean))).sort());

const historicoGastosData = computed(() => {
  const labels = [];
  const monthlySpending = {};
  const today = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const label = d.toLocaleString('pt-BR', { month: 'short' });
    labels.push(label.charAt(0).toUpperCase() + label.slice(1));
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    monthlySpending[key] = 0;
  }
  allItems.value.forEach(item => {
    if (item.tipo === 'Despesa' && item.data) {
      const key = item.data.substring(0, 7);
      if (Object.prototype.hasOwnProperty.call(monthlySpending, key)) {
        monthlySpending[key] += parseFloat(item.valorParcela || item.valor) || 0;
      }
    }
  });
  const data = Object.values(monthlySpending);
  return {
    labels,
    datasets: [{ label: 'Total de Gastos', backgroundColor: '#7C3AED', data, borderRadius: 6 }]
  }
});

const gastosPorCategoriaABCData = computed(() => {
  const gastosAgrupados = itensFiltradosParaResumoEGraficos.value.filter(item => item.tipo === 'Despesa' && item.categoria).reduce((acc, gasto) => {
    acc[gasto.categoria] = (acc[gasto.categoria] || 0) + (parseFloat(gasto.valorParcela || gasto.valor) || 0);
    return acc;
  }, {});
  const sortedCategorias = Object.entries(gastosAgrupados).sort((a, b) => b[1] - a[1]).slice(0, 5);
  return {
    labels: sortedCategorias.map(item => item[0]),
    datasets: [{ label: 'Gasto por Categoria', backgroundColor: ['#5A189A', '#7B2CBF', '#8B5CF6', '#A78BFA', '#C4B5FD'], data: sortedCategorias.map(item => item[1]), borderRadius: 6 }]
  };
});

const temDadosCategoria = computed(() => gastosPorCategoriaABCData.value.labels.length > 0);
const chartOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}` } } },
  scales: {
    y: { beginAtZero: true, grid: { color: isDarkMode.value ? 'rgba(148, 163, 184, 0.1)' : 'rgba(226, 232, 240, 1)' }, ticks: { color: isDarkMode.value ? '#94a3b8' : '#64748b' } },
    x: { grid: { display: false }, ticks: { color: isDarkMode.value ? '#94a3b8' : '#64748b' } }
  }
};
const abcChartOptions = {
  ...chartOptions,
  indexAxis: 'y',
};

const exportarPDF = () => {
  isExporting.value = true;
  try {
    const doc = new jsPDF();
    doc.text(`Relatório Financeiro de ${mesSelecionado.value}/${anoSelecionado.value}`, 14, 22);
    doc.autoTable({
      startY: 35,
      head: [['Data', 'Descrição', 'Valor', 'Tipo', 'Categoria']],
      body: itensFiltradosParaLista.value.map(item => [
        formatDate(item.data),
        item.descricao,
        `${item.tipo === 'Receita' ? '+' : '-'} ${formatCurrency(item.valorParcela || item.valor)}`,
        item.tipo,
        item.categoria || ''
      ]),
    });
    doc.save(`relatorio-financeiro.pdf`);
  } finally {
    isExporting.value = false;
  }
};

const exportarExcel = () => {
  isExporting.value = true;
  try {
    const dataExport = itensFiltradosParaLista.value.map(item => ({
      Data: formatDate(item.data),
      Descrição: item.descricao,
      Valor: (item.tipo === 'Receita' ? 1 : -1) * (item.valorParcela || item.valor),
      Tipo: item.tipo,
      Categoria: item.categoria || '',
      Banco: item.subtipo === 'cartao' ? item.banco : '',
      Parcela: item.subtipo === 'cartao' ? `${item.numeroParcela}/${item.totalParcelas}` : ''
    }));
    const ws = XLSX.utils.json_to_sheet(dataExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Relatorio`);
    XLSX.writeFile(wb, `relatorio-financeiro.xlsx`);
  } finally {
    isExporting.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const formatCurrency = (value) => (typeof value === 'number' ? value : 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const getItemColor = (item) => {
  if (item.tipo === 'Receita') return isDarkMode.value ? '#4ADE80' : '#22C55E';
  if (item.subtipo === 'cartao') return (bankColorVariants[item.banco] || bankColorVariants['Outro']).color[isDarkMode.value ? 'dark' : 'light'];
  return isDarkMode.value ? '#F87171' : '#EF4444';
}

const getCategoryIcon = (category) => categoryIcons[category] || 'sell';
const getStatusClass = (status) => {
  const statusClasses = {
    PENDENTE: { bg: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
    PAGO: { bg: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
    CONGELADO: { bg: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' }
  };
  return statusClasses[status] || { bg: 'bg-slate-100 text-slate-700' };
};

onMounted(() => {
  mesSelecionado.value = String(new Date().getMonth() + 1).padStart(2, '0');
  anoSelecionado.value = String(new Date().getFullYear());
});
</script>

<style scoped>
.panel {
  @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20 p-4 sm:p-6;
}

.panel-title {
  @apply text-lg font-bold tracking-tight text-slate-800 dark:text-slate-100 mb-4;
}

.form-select {
  @apply h-11 appearance-none block w-full px-4 text-sm font-normal text-slate-700 dark:text-slate-200 bg-slate-100/80 dark:bg-slate-900/50 rounded-xl transition ease-in-out ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-[#5A189A] cursor-pointer;
}

.form-label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300;
}

.btn {
  @apply inline-flex items-center justify-center gap-2 font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60;
}

.btn-secondary {
  @apply btn bg-white/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm;
}

.stat-card {
  @apply flex items-center gap-4 rounded-2xl p-4 ring-1 ring-black/5 dark:ring-white/10 bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg;
}

.stat-icon {
  @apply size-12 grid place-items-center rounded-xl text-2xl;
}

/* Removido material-symbols-outlined */
.stat-label {
  @apply text-sm uppercase tracking-wider text-slate-500 dark:text-slate-400;
}

.stat-value {
  @apply text-2xl font-bold tracking-tight text-slate-800 dark:text-slate-100;
}

.empty-state {
  @apply flex flex-col h-full items-center justify-center text-center text-slate-500 dark:text-slate-400;
}

.badge {
  @apply inline-flex items-center px-2.5 py-1 text-[11px] font-semibold rounded-full;
}

.mobile-card {
  @apply bg-white dark:bg-slate-800/80 shadow-lg rounded-lg p-4 border-l-4;
}
</style>